import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Upload, CheckCircle2, AlertCircle, FileText } from "lucide-react";

/*
 * PUBLIC customer-facing CV upload page — mobile-first, tokenized, no
 * login. Deliberately a SEPARATE page/token from PublicBookingPage
 * (/book/:token) — this is NOT part of Manage Booking (no reschedule/
 * cancel here). No internal IDs, Drive folder paths, or staff data are
 * ever exposed. The backend derives contact/opportunity/product
 * exclusively from the validated token — nothing here is client-supplied.
 */
export default function PublicCvUploadPage() {
  const { token } = useParams();
  const fileRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/documents/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(errDetail(e, "This upload link is not valid.")));
  }, [token]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setError("Choose a file first."); return; }
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/public/documents/${token}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      setError(errDetail(e));
    } finally { setBusy(false); }
  };

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--muted))]/40 px-4">
        <Card className="w-full max-w-md"><CardContent className="pt-8 pb-8 text-center">
          <AlertCircle className="h-8 w-8 text-[hsl(var(--danger))] mx-auto mb-3" />
          <p data-testid="public-upload-error" className="text-sm">{error}</p>
          <p className="text-xs text-muted-foreground mt-2">Please contact Aussie Pathways for a new upload link.</p>
        </CardContent></Card>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-3"><Skeleton className="h-24" /><Skeleton className="h-32" /></div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--muted))]/40 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center" data-testid="public-upload-confirmation">
            <CheckCircle2 className="h-10 w-10 text-[hsl(var(--success))] mx-auto mb-3" />
            <h2 className="font-heading text-lg font-semibold">CV received ✓</h2>
            <p className="text-sm text-muted-foreground mt-2">Our process team can now review your profile before your consultation.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]/40 px-4 py-8 flex justify-center">
      <div className="w-full max-w-md space-y-4" data-testid="public-cv-upload-page">
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold">Upload your latest CV</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {info.first_name ? `Hi ${info.first_name}, please` : "Please"} upload your latest CV so our process team can
            review your {info.product_label} profile before your consultation.
          </p>
        </div>

        {error && (
          <div data-testid="public-upload-inline-error" className="text-sm bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] rounded-md px-3 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        <Card>
          <CardContent className="pt-4 pb-4 space-y-3">
            <label
              htmlFor="cv-file-input"
              className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <FileText className="h-7 w-7 text-muted-foreground" />
              <span className="text-sm font-medium">{fileName || "Choose your CV file"}</span>
              <span className="text-xs text-muted-foreground">
                Accepted: {info.accepted_extensions.join(", ")} · Max {info.max_size_mb} MB
              </span>
            </label>
            <input
              id="cv-file-input" data-testid="public-cv-file-input" ref={fileRef} type="file"
              accept={info.accepted_extensions.join(",")} className="hidden"
              onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
            />
            <Button data-testid="public-cv-upload-button" className="w-full h-11" disabled={busy || !fileName} onClick={upload}>
              <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading…" : "Upload CV"}
            </Button>
          </CardContent>
        </Card>
        <p className="text-[11px] text-center text-muted-foreground">Aussie Pathways · This link is secure and personal to you</p>
      </div>
    </div>
  );
}
